import React, { useEffect, useRef } from 'react';
import styled from '@grebban/style-system-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Button from 'components/Button';
import ChevronRight from 'assets/media/icons/ChevronRight';
import ChevronLeft from 'assets/media/icons/ChevronLeft';
import { NavigationSubmenuSection } from './navigation/NavigationSubmenuSection';
import { HeaderContactBlock } from './HeaderContactBlock';
import { type MainMenuItem, type SubmenuSection, type UtilityMenuItem } from './types';
import { OVERLAY_GRID_COLUMNS } from './utils';

/** Full-viewport overlay: fades in on open; content stays mounted. Close is instant (see transition comment). */
const OverlayWrapper = styled('div')<{ $isOpen: boolean }>`
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    z-index: 98;
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    /* Safari: fading the overlay out makes the address bar and bottom toolbar pick up the overlay
       colors. No fade on close avoids that; opening still fades in. */
    transition: ${({ $isOpen }) =>
        $isOpen ? 'opacity var(--transition-primary-fast), visibility var(--transition-primary-fast)' : 'none'};
`;

/** Full-viewport semi-transparent layer; click closes overlay. */
const OverlayBackdrop = styled('div')`
    position: absolute;
    inset: 0;
    background-color: var(--overlay-overlay-background);
    z-index: 0;
`;

/** Menu panel (OVERLAY_GRID_COLUMNS / var(--grid-columns) of viewport). Left or right depending on anchor. */
const OverlayPanel = styled('div')<{ $anchor: 'left' | 'right' }>`
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 1;
    width: calc(100% * ${OVERLAY_GRID_COLUMNS} / var(--grid-columns));
    max-width: calc(100vw * ${OVERLAY_GRID_COLUMNS} / var(--grid-columns));
    height: 100%;
    overflow-y: auto;
    background-color: var(--ui-background);

    box-shadow: ${({ $anchor }) =>
        $anchor === 'right' ? '-4px 0 24px rgba(0,0,0,0.08)' : '4px 0 24px rgba(0,0,0,0.08)'};
    ${({ $anchor }) => ($anchor === 'right' ? 'right: 0; left: auto;' : 'left: 0; right: auto;')}
`;

/** Inner content: stacks sections and contact; grid lives inside each NavigationSubmenuSection. */
const OverlayInner = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 60px;
    padding: 88px var(--grid-padding) 20px;
    min-height: 100%;
    color: var(--ui-secondary-aa);
`;

const SectionsWrapper = styled('div')`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 60px;
`;

/** Mobile: full-width panel with padding; contains the slide viewport. */
const MobileOverlayPanel = styled('div')`
    position: absolute;
    inset: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 92px 10px 20px 10px;
    background-color: var(--ui-background);
    color: var(--ui-primary-aa);
    overflow-y: auto;
`;

/** Mobile: viewport for sliding panels; hides overflow so panels can slide in/out. */
const MobileSlideViewport = styled('div')`
    position: relative;
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
`;

/** Mobile: one sliding panel (main menu or submenu). $offset: 0 = in view, -110 = left, 110 = right (extra % = gap so chevrons don’t meet). */
const MobileSlidePanel = styled('div')<{ $offset: number }>`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow-y: auto;
    transform: translateX(${({ $offset }) => $offset}%);
    transition: transform var(--transition-primary-medium);

    /* In-view panel on top so outgoing content (e.g. chevrons) doesn’t show through. */
    z-index: ${({ $offset }) => ($offset === 0 ? 1 : 0)};
    background-color: var(--ui-background);
`;

const ButtonContent = styled('span')`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const MobileBackButton = styled(Button)`
    display: flex;
    align-items: center;
    padding-bottom: 12px;
    width: 100%;
    justify-content: flex-start;
    text-align: left;
`;

const MobileNavList = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const MobileNavButton = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: left;
`;

const MobileNavLink = styled(Button)`
    display: block;
    width: 100%;
    text-align: left;
`;

const hasSubmenu = (item: MainMenuItem | UtilityMenuItem): boolean =>
    !!(item.content?.submenu || (item as MainMenuItem).links?.length);

export interface MenuOverlayProps {
    /** Id for the overlay element (e.g. for aria-controls on the trigger). */
    id?: string;
    isOpen: boolean;
    variant: 'desktop' | 'mobile';
    /** Which side the overlay opens from (main menu = left, utility menu = right). */
    anchor?: 'left' | 'right';
    /** For mobile: label of the currently expanded submenu (for aria-expanded on menu rows). */
    activeSubMenu?: string;
    /** Normalised CMS submenu sections (with or without title). Contact block is always rendered at the bottom. */
    submenuSections?: SubmenuSection[] | null;
    /** For variant="mobile": top-level menu items (main + utility combined, in that order). */
    mobileMenuItems?: (MainMenuItem | UtilityMenuItem)[] | null;
    /** For variant="mobile": set active submenu label (expand item). */
    onSetActiveSubMenu?: (label: string) => void;
    contactEmail?: string;
    contactPhone?: string;
    findRetailerLabel?: string;
    /** Called to close the overlay (e.g. backdrop click, Escape, or link click in submenu). */
    onCloseOverlay?: () => void;
}

const DEFAULT_EMAIL = 'info@fogia.se';
const DEFAULT_PHONE = '+46 (0) 8 506 140 00';

export const MenuOverlay = ({
    id,
    isOpen,
    variant,
    anchor = 'left',
    activeSubMenu = '',
    submenuSections,
    mobileMenuItems = [],
    onSetActiveSubMenu,
    contactEmail = DEFAULT_EMAIL,
    contactPhone = DEFAULT_PHONE,
    findRetailerLabel,
    onCloseOverlay,
}: MenuOverlayProps) => {
    const { t } = useTranslation();
    const resolvedFindRetailerLabel = findRetailerLabel ?? t('header.find_retailer');

    const showSubmenu = variant === 'mobile' && submenuSections != null && submenuSections.length > 0;

    // Mobile: keep last submenu sections so the sliding-out panel still has content until the transition ends.
    const mobileOutgoingSubmenuSectionsRef = useRef<SubmenuSection[]>([]);
    if (submenuSections != null && submenuSections.length > 0) {
        mobileOutgoingSubmenuSectionsRef.current = submenuSections;
    }
    useEffect(() => {
        if (variant !== 'mobile' || showSubmenu) return;
        const timeoutId = setTimeout(() => {
            mobileOutgoingSubmenuSectionsRef.current = [];
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [variant, showSubmenu]);

    // Mobile: when closing (showSubmenu false), keep showing previous sections until they've slid out.
    const mobileSectionsForSubmenuPanel =
        (submenuSections != null && submenuSections.length > 0
            ? submenuSections
            : mobileOutgoingSubmenuSectionsRef.current) ?? [];

    // Close overlay when user clicks a link inside it (including same-URL links). Use ref + capture to avoid lint on non-interactive element.
    const overlayPanelRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = overlayPanelRef.current;
        if (!el) return;
        const handleClick = (e: Event) => {
            const link = (e.target as Element).closest?.('a[href]');
            if (!link) return;
            const href = link.getAttribute('href') ?? '';
            if (href.startsWith('mailto:') || href.startsWith('tel:')) return;
            onCloseOverlay?.();
        };
        el.addEventListener('click', handleClick, true);
        return () => el.removeEventListener('click', handleClick, true);
    }, [onCloseOverlay, variant]);

    // Close overlay when route changes (e.g. link in header bar or overlay)
    const { pathname } = useLocation();
    const prevPathnameRef = useRef(pathname);
    useEffect(() => {
        if (prevPathnameRef.current !== pathname) {
            prevPathnameRef.current = pathname;
            onCloseOverlay?.();
        }
    }, [pathname, onCloseOverlay]);

    return (
        <OverlayWrapper $isOpen={isOpen} aria-hidden={!isOpen} id={id} data-theme={isOpen ? 'orange' : 'light'}>
            <>
                <OverlayBackdrop
                    role="button"
                    tabIndex={-1}
                    onClick={onCloseOverlay}
                    onKeyDown={(e) => e.key === 'Escape' && onCloseOverlay?.()}
                    aria-label={t('header.close_menu')}
                />
                {variant === 'mobile' ? (
                    <MobileOverlayPanel
                        ref={overlayPanelRef}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('header.menu_overlay_label')}
                    >
                        <MobileSlideViewport>
                            <MobileSlidePanel $offset={showSubmenu ? -110 : 0}>
                                <MobileNavList>
                                    {(mobileMenuItems ?? []).map((item, index) => {
                                        const label = item.label ?? item.link?.text ?? '';
                                        const hasSub = hasSubmenu(item);
                                        const href = item.link?.url ?? '#';
                                        const key = `${label || 'nav'}-${index}`;
                                        if (hasSub) {
                                            return (
                                                <MobileNavButton
                                                    key={key}
                                                    theme="textButton"
                                                    typography="Primary/Mobile/18_120_510_-3"
                                                    type="button"
                                                    aria-label={label}
                                                    onClick={() => onSetActiveSubMenu?.(label)}
                                                    aria-expanded={activeSubMenu === label}
                                                    aria-haspopup="true"
                                                >
                                                    <ButtonContent width="100%" justifyContent="space-between">
                                                        {label}
                                                        <ChevronRight size="xsmall" />
                                                    </ButtonContent>
                                                </MobileNavButton>
                                            );
                                        }
                                        return (
                                            <MobileNavLink
                                                key={key}
                                                theme="textButton"
                                                typography="Primary/Mobile/18_120_510_-3"
                                                url={href}
                                                aria-label={label}
                                            >
                                                {label}
                                            </MobileNavLink>
                                        );
                                    })}
                                </MobileNavList>
                                <HeaderContactBlock
                                    email={contactEmail}
                                    phone={contactPhone}
                                    findRetailerLabel={resolvedFindRetailerLabel}
                                />
                            </MobileSlidePanel>
                            <MobileSlidePanel $offset={showSubmenu ? 0 : 110}>
                                <MobileBackButton
                                    theme="textButton"
                                    typography="Primary/Mobile/13_100_510_-1"
                                    type="button"
                                    aria-label={t('header.back_to_menu')}
                                    onClick={() => onSetActiveSubMenu?.('')}
                                >
                                    <ButtonContent>
                                        <ChevronLeft size="xsmall" />
                                        {t('header.back_to_menu')}
                                    </ButtonContent>
                                </MobileBackButton>
                                <SectionsWrapper>
                                    {mobileSectionsForSubmenuPanel.map((section, index) => (
                                        <NavigationSubmenuSection
                                            key={`${section.title ?? 'section'}-${index}`} // eslint-disable-line react/no-array-index-key -- index needed when section titles duplicate
                                            title={section.title}
                                            columnLayout={section.columnLayout}
                                            items={section.items}
                                            forceSingleColumn
                                            onCloseOverlay={onCloseOverlay}
                                        />
                                    ))}
                                </SectionsWrapper>
                            </MobileSlidePanel>
                        </MobileSlideViewport>
                    </MobileOverlayPanel>
                ) : (
                    <OverlayPanel
                        ref={overlayPanelRef}
                        $anchor={anchor}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('header.menu_overlay_label')}
                    >
                        <OverlayInner>
                            <SectionsWrapper>
                                {submenuSections != null &&
                                    submenuSections.length > 0 &&
                                    submenuSections.map((section, index) => (
                                        <NavigationSubmenuSection
                                            key={`${section.title ?? 'section'}-${index}`} // eslint-disable-line react/no-array-index-key -- index needed when section titles duplicate
                                            title={section.title}
                                            columnLayout={section.columnLayout}
                                            items={section.items}
                                            onCloseOverlay={onCloseOverlay}
                                        />
                                    ))}
                            </SectionsWrapper>
                            <HeaderContactBlock
                                email={contactEmail}
                                phone={contactPhone}
                                findRetailerLabel={resolvedFindRetailerLabel}
                            />
                        </OverlayInner>
                    </OverlayPanel>
                )}
            </>
        </OverlayWrapper>
    );
};
